<template>
  <div class="container-fluid">
    <div class="d-flex justify-content-between align-items-center mb-4">
      <h2>Логи действий администратора</h2>
    </div>

    <!-- Фильтры -->
    <div class="card mb-4">
      <div class="card-header">
        <h5 class="mb-0">Фильтры</h5>
      </div>
      <div class="card-body">
        <div class="row">
          <div class="col-md-3">
            <label class="form-label">Тип действия</label>
            <select v-model="filters.action_type" class="form-select">
              <option value="">Все действия</option>
              <option value="create">Создание</option>
              <option value="update">Обновление</option>
              <option value="delete">Удаление</option>
              <option value="login">Вход в систему</option>
            </select>
          </div>
          <div class="col-md-3">
            <label class="form-label">Тип сущности</label>
            <select v-model="filters.entity_type" class="form-select">
              <option value="">Все сущности</option>
              <option value="user">Пользователи</option>
              <option value="case">Кейсы</option>
              <option value="setting">Настройки</option>
              <option value="case_member">Участники кейсов</option>
            </select>
          </div>
          <div class="col-md-3">
            <label class="form-label">Уровень важности</label>
            <select v-model="filters.severity" class="form-select">
              <option value="">Все уровни</option>
              <option value="INFO">Информация</option>
              <option value="WARNING">Предупреждение</option>
              <option value="CRITICAL">Критично</option>
            </select>
          </div>
          <div class="col-md-3">
            <label class="form-label">ID сущности</label>
            <input v-model="filters.entity_id" type="text" class="form-control" placeholder="Введите ID">
          </div>
        </div>
        <div class="row mt-3">
          <div class="col-md-6">
            <button @click="applyFilters" class="btn btn-primary me-2">
              <i class="bi bi-funnel"></i> Применить фильтры
            </button>
            <button @click="clearFilters" class="btn btn-outline-secondary">
              <i class="bi bi-x-circle"></i> Очистить
            </button>
          </div>
          <div class="col-md-6">
            <div class="d-flex gap-2 justify-content-end">
              <button class="btn btn-outline-secondary" @click="exportLogs">
                <i class="bi bi-download"></i> Экспорт
              </button>
              <button class="btn btn-outline-info" @click="loadStats">
                <i class="bi bi-bar-chart"></i> Статистика
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Таблица логов -->
    <div class="card">
      <div class="card-body">
        <div v-if="loading" class="text-center py-4">
          <div class="spinner-border" role="status">
            <span class="visually-hidden">Загрузка...</span>
          </div>
        </div>

        <div v-else-if="logs.length === 0" class="text-center py-4">
          <p class="text-muted">Логи не найдены</p>
        </div>

        <div v-else>
          <div class="table-responsive">
            <table class="table table-hover">
              <thead>
                <tr>
                  <th>Время</th>
                  <th>Пользователь</th>
                  <th>Действие</th>
                  <th>Сущность</th>
                  <th>Описание</th>
                  <th>Важность</th>
                  <th>IP адрес</th>
                  <th>Действия</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="log in logs" :key="log.id" :class="getSeverityClass(log.severity)">
                  <td>
                    <small>{{ formatDateTime(log.timestamp) }}</small>
                  </td>
                  <td>
                    <strong>{{ log.user_name || 'Неизвестно' }}</strong>
                    <br>
                    <small class="text-muted">ID: {{ log.user_id }}</small>
                  </td>
                  <td>
                    <span :class="getActionBadgeClass(log.action_type)">
                      {{ getActionLabel(log.action_type) }}
                    </span>
                  </td>
                  <td>
                    <strong>{{ getEntityLabel(log.entity_type) }}</strong>
                    <br>
                    <small class="text-muted">ID: {{ log.entity_id }}</small>
                  </td>
                  <td>{{ log.description || '-' }}</td>
                  <td>
                    <span :class="getSeverityBadgeClass(log.severity)">
                      {{ getSeverityLabel(log.severity) }}
                    </span>
                  </td>
                  <td>
                    <small>{{ log.ip_address || '-' }}</small>
                  </td>
                  <td>
                    <button @click="viewDetails(log)" class="btn btn-sm btn-outline-info">
                      <i class="bi bi-eye"></i>
                    </button>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <!-- Пагинация -->
          <nav v-if="totalPages > 1" aria-label="Навигация по страницам">
            <ul class="pagination justify-content-center">
              <li class="page-item" :class="{ disabled: currentPage === 1 }">
                <button class="page-link" @click="changePage(currentPage - 1)" :disabled="currentPage === 1">
                  Предыдущая
                </button>
              </li>
              <li v-for="page in visiblePages" :key="page" class="page-item" :class="{ active: page === currentPage }">
                <button class="page-link" @click="changePage(page)">{{ page }}</button>
              </li>
              <li class="page-item" :class="{ disabled: currentPage === totalPages }">
                <button class="page-link" @click="changePage(currentPage + 1)" :disabled="currentPage === totalPages">
                  Следующая
                </button>
              </li>
            </ul>
          </nav>
        </div>
      </div>
    </div>

    <!-- Модальное окно с деталями -->
    <div class="modal fade" id="detailsModal" tabindex="-1" aria-labelledby="detailsModalLabel" aria-hidden="true">
      <div class="modal-dialog modal-lg">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title" id="detailsModalLabel">Детали действия</h5>
            <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
          </div>
          <div class="modal-body">
            <div v-if="selectedLog">
              <div class="row mb-3">
                <div class="col-md-6">
                  <strong>Время:</strong> {{ formatDateTime(selectedLog.timestamp) }}
                </div>
                <div class="col-md-6">
                  <strong>Пользователь:</strong> {{ selectedLog.user_name }} (ID: {{ selectedLog.user_id }})
                </div>
              </div>
              <div class="row mb-3">
                <div class="col-md-6">
                  <strong>Действие:</strong> {{ getActionLabel(selectedLog.action_type) }}
                </div>
                <div class="col-md-6">
                  <strong>Сущность:</strong> {{ getEntityLabel(selectedLog.entity_type) }} (ID: {{ selectedLog.entity_id }})
                </div>
              </div>
              <div class="row mb-3">
                <div class="col-md-6">
                  <strong>IP адрес:</strong> {{ selectedLog.ip_address || '-' }}
                </div>
                <div class="col-md-6">
                  <strong>User Agent:</strong> 
                  <small>{{ selectedLog.user_agent || '-' }}</small>
                </div>
              </div>
              <div class="mb-3">
                <strong>Описание:</strong> {{ selectedLog.description || '-' }}
              </div>
              <div v-if="selectedLog.changes && Object.keys(selectedLog.changes).length > 0">
                <strong>Изменения:</strong>
                <pre class="bg-light p-3 mt-2 rounded">{{ JSON.stringify(selectedLog.changes, null, 2) }}</pre>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Модальное окно со статистикой -->
    <div class="modal fade" id="statsModal" tabindex="-1" aria-labelledby="statsModalLabel" aria-hidden="true">
      <div class="modal-dialog modal-lg">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title" id="statsModalLabel">Статистика логов</h5>
            <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
          </div>
          <div class="modal-body">
            <div v-if="stats">
              <div class="row">
                <div class="col-md-4">
                  <div class="card text-center">
                    <div class="card-body">
                      <h3 class="text-primary">{{ stats.total_actions }}</h3>
                      <p class="card-text">Всего действий</p>
                    </div>
                  </div>
                </div>
                <div class="col-md-8">
                  <h6>По типам действий:</h6>
                  <div v-for="stat in stats.action_types" :key="stat.type" class="d-flex justify-content-between">
                    <span>{{ getActionLabel(stat.type) }}:</span>
                    <strong>{{ stat.count }}</strong>
                  </div>
                  
                  <h6 class="mt-3">По типам сущностей:</h6>
                  <div v-for="stat in stats.entity_types" :key="stat.type" class="d-flex justify-content-between">
                    <span>{{ getEntityLabel(stat.type) }}:</span>
                    <strong>{{ stat.count }}</strong>
                  </div>
                  
                  <h6 class="mt-3">По уровням важности:</h6>
                  <div v-for="stat in stats.severity_levels" :key="stat.level" class="d-flex justify-content-between">
                    <span>{{ getSeverityLabel(stat.level) }}:</span>
                    <strong>{{ stat.count }}</strong>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { Modal } from 'bootstrap';
import axiosInstance from '../services/axiosInstance';

export default {
  name: 'AdminLogsView',
  data() {
    return {
      logs: [],
      loading: false,
      currentPage: 1,
      totalPages: 1,
      pageSize: 20,
      totalItems: 0,
      selectedLog: null,
      stats: null,
      filters: {
        action_type: '',
        entity_type: '',
        severity: '',
        entity_id: ''
      }
    };
  },
  computed: {
    visiblePages() {
      const pages = [];
      const start = Math.max(1, this.currentPage - 2);
      const end = Math.min(this.totalPages, this.currentPage + 2);
      
      for (let i = start; i <= end; i++) {
        pages.push(i);
      }
      return pages;
    }
  },
  mounted() {
    this.loadLogs();
  },
  methods: {
    async loadLogs() {
      this.loading = true;
      try {
        const params = {
          page: this.currentPage,
          size: this.pageSize,
          ...this.filters
        };
        
        // Удаляем пустые фильтры
        Object.keys(params).forEach(key => {
          if (params[key] === '' || params[key] === null || params[key] === undefined) {
            delete params[key];
          }
        });
        
        const response = await axiosInstance.get('/admin-logs', { params });
        this.logs = response.data.items;
        this.totalPages = response.data.pages;
        this.totalItems = response.data.total;
      } catch (error) {
        console.error('Ошибка загрузки логов:', error);
        this.$toast?.error('Ошибка загрузки логов');
      } finally {
        this.loading = false;
      }
    },
    
    async loadStats() {
      try {
        const response = await axiosInstance.get('/admin-logs/stats');
        this.stats = response.data;
        const modal = new Modal(document.getElementById('statsModal'));
        modal.show();
      } catch (error) {
        console.error('Ошибка загрузки статистики:', error);
        this.$toast?.error('Ошибка загрузки статистики');
      }
    },
    
    changePage(page) {
      if (page >= 1 && page <= this.totalPages) {
        this.currentPage = page;
        this.loadLogs();
      }
    },
    
    applyFilters() {
      this.currentPage = 1;
      this.loadLogs();
    },
    
    clearFilters() {
      this.filters = {
        action_type: '',
        entity_type: '',
        severity: '',
        entity_id: ''
      };
      this.currentPage = 1;
      this.loadLogs();
    },
    
    viewDetails(log) {
      this.selectedLog = log;
      const modal = new Modal(document.getElementById('detailsModal'));
      modal.show();
    },
    
    exportLogs() {
      // Здесь можно добавить логику экспорта
      this.$toast?.info('Функция экспорта будет добавлена позже');
    },
    
    formatDateTime(timestamp) {
      return new Date(timestamp).toLocaleString('ru-RU');
    },
    
    getActionLabel(action) {
      const labels = {
        'create': 'Создание',
        'update': 'Обновление',
        'delete': 'Удаление',
        'login': 'Вход'
      };
      return labels[action] || action;
    },
    
    getEntityLabel(entity) {
      const labels = {
        'user': 'Пользователь',
        'case': 'Кейс',
        'setting': 'Настройка',
        'case_member': 'Участник кейса'
      };
      return labels[entity] || entity;
    },
    
    getSeverityLabel(severity) {
      const labels = {
        'INFO': 'Информация',
        'WARNING': 'Предупреждение',
        'CRITICAL': 'Критично'
      };
      return labels[severity] || severity;
    },
    
    getActionBadgeClass(action) {
      const classes = {
        'create': 'badge bg-success',
        'update': 'badge bg-warning',
        'delete': 'badge bg-danger',
        'login': 'badge bg-info'
      };
      return classes[action] || 'badge bg-secondary';
    },
    
    getSeverityBadgeClass(severity) {
      const classes = {
        'INFO': 'badge bg-info',
        'WARNING': 'badge bg-warning',
        'CRITICAL': 'badge bg-danger'
      };
      return classes[severity] || 'badge bg-secondary';
    },
    
    getSeverityClass(severity) {
      const classes = {
        'CRITICAL': 'table-danger',
        'WARNING': 'table-warning'
      };
      return classes[severity] || '';
    }
  }
};
</script>

<style scoped>
.table-responsive {
  max-height: 600px;
}

pre {
  max-height: 300px;
  overflow-y: auto;
  font-size: 0.875rem;
}

.card {
  box-shadow: 0 0.125rem 0.25rem rgba(0, 0, 0, 0.075);
}

.badge {
  font-size: 0.75rem;
}

.table td {
  vertical-align: middle;
}

.table td small {
  color: #6c757d;
}
</style> 